/**
 * The `mod` bridge (RFC 0267 §5b): the sandbox-to-host RPC that lets an app
 * view read and write its data. A view runs in a network-blocked iframe and
 * cannot touch the repo directly; `window.mod` is a shim (injected below) whose
 * every call is a postMessage the host answers by running a real mod-core
 * mutation, scoped to the app's own data type. Because instances are synced
 * Automerge documents, a write persists and syncs to every client for free —
 * there is no separate save or sync path.
 *
 * S2 scope: the app's OWN type (mod.type.*). Cross-interface reads
 * (mod.docs/mod.sessions) and the capability grants come next.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import {
  answerComment,
  type Candidate,
  type CommentEntry,
  catalogRows,
  createComment,
  createDocument,
  type DocumentDoc,
  deleteComment,
  deleteDocument,
  deliverMentions,
  installedApps,
  loadComments,
  locateComment,
  type Mention,
  type MentionKind,
  mimeFromPath,
  normalizeDocumentPath,
  type ProjectDoc,
  patchDocumentData,
  projectAuthor,
  projectCandidates,
  projectComments,
  projectDocuments,
  projectDocumentsOfType,
  projectSessions,
  projectSessionTranscript,
  projectSources,
  projectThreads,
  readApp,
  readDocumentContent,
  readDocumentData,
  renameDocument,
  resolveComment,
  searchCatalogs,
  worldCatalogs,
  writeDocumentContent,
  writeDocumentData,
} from '@mod/core/model'
// `sessions.send` is a queued RUN, not a plain append, and enqueueRun is the
// only path that both writes the message and queues the turn (and that knows a
// captured session must not get one). The agent's own send_message uses it for
// the same reason; a second implementation is how the two drift.
import { enqueueRun } from '@mod/core/run'
import { parseCodeId } from '../../services/code/codeFile'
import {
  ANONYMOUS_AUTHOR,
  commentAuthor,
} from '../../services/identity/commentAuthor'
import { personChangeMessage } from '../../services/identity/localPerson'
import {
  commentReader,
  threadWaiting,
} from '../../services/sessions/commentReader'
import { sessionFace } from '../../services/sessions/sessionFace'
import { readWorldPointer } from '../../services/world/world'
import type { BridgeHandler } from '../capability'
import { prefsHandler } from '../prefs'
import { installCommentElements } from '../ui/commentElements'
import { installTaskElements } from '../ui/taskElements'
import { vcsHandler } from '../vcs'
import {
  FORWARDED_CODES,
  NATIVE_EDITING_CODES,
  SHIFT_FORWARDED_CODES,
} from './frameShortcuts'
import { handleTasksCall } from './taskInterface'

/**
 * Which mention KINDS this app may name (RFC 0292 S5).
 *
 * A mention crosses interfaces: it points at a session or a document, which are
 * exactly the two namespaces the bridge already gates. So it is gated by the
 * SAME grants rather than a new one — an app that cannot list sessions must not
 * be able to wake one by naming an id it guessed, and an app with no grants at
 * all can still comment, just not point anywhere.
 */
function mentionableKinds(
  grants: string[],
  writeGrants: string[],
): MentionKind[] {
  const out: MentionKind[] = []
  if (grants.includes('sessions')) out.push('session')
  if (grants.includes('documents') || writeGrants.includes('documents')) {
    out.push('document')
  }
  if (grants.includes('tasks') || writeGrants.includes('tasks'))
    out.push('task')
  return out
}

/**
 * The mentions a view sent, kept only where the app may name that kind.
 *
 * DROPS rather than throws, deliberately: an ungranted mention is a chip that
 * will not be stored, and the comment itself is still worth writing. The view
 * learns what landed from the ids returned.
 */
function allowedMentions(
  raw: unknown,
  grants: string[],
  writeGrants: string[],
): Mention[] {
  if (!Array.isArray(raw) || raw.length === 0) return []
  const kinds = new Set(mentionableKinds(grants, writeGrants))
  const out: Mention[] = []
  for (const m of raw) {
    if (!m || typeof m !== 'object') continue
    const e = m as { kind?: unknown; id?: unknown; at?: unknown; len?: unknown }
    const kind: MentionKind =
      e.kind === 'document'
        ? 'document'
        : e.kind === 'task'
          ? 'task'
          : 'session'
    if (!kinds.has(kind)) continue
    const id = String(e.id ?? '')
    if (!id) continue
    const at = Number(e.at)
    const len = Number(e.len)
    out.push({
      kind,
      id,
      at: Number.isFinite(at) ? at : 0,
      len: Number.isFinite(len) ? len : 0,
    })
  }
  return out
}

/** The wire message an app view posts to the host, and the host's reply. */
interface BridgeCall {
  __modBridge: 'call'
  id: number
  method: string
  args: unknown[]
}

/**
 * The sandbox form of `@mod/sdk/ui` (RFC 0325).
 *
 * The host injects the implementation before an app's modules run. This tiny
 * module is only the authoring adapter: a plain-module app imports a named
 * component and receives the corresponding live custom element. The element,
 * its task rules, and its styles remain host-owned.
 */
const TASK_UI_MODULE = `
const element = (tag, props = {}) => Object.assign(document.createElement(tag), props)
export const TaskRow = (props) => element('mod-task-row', props)
export const TaskList = (props) => element('mod-task-list', props)
export const TaskChip = (props) => element('mod-task-chip', props)
export const CommentRow = (props) => element('mod-comment-row', props)
export const CommentThread = (props) => element('mod-comment-thread', props)
export const CommentChip = (props) => element('mod-comment-chip', props)
`

/**
 * The shim injected into the view's iframe. Defines `window.mod.type` as a
 * type-first RPC facade (list/create/update/change/get) that resolves against
 * the host. The dataType is baked in so a view cannot reach another app's type
 * even if it passes a different one; the host ignores any type argument and
 * uses SCOPED. A `mod:change` message from the host re-fires subscribers so the
 * view re-renders live when another client writes.
 */
export function bridgeShim(
  dataType: string,
  surface = 'canvas',
  instanceId?: string,
  /** RFC 0275: the app declared it wants real handles, so expose mod.open. */
  sync?: 'documents',
): string {
  const taskUiUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(TASK_UI_MODULE)}`
  const importMap = JSON.stringify({ imports: { '@mod/sdk/ui': taskUiUrl } })
  return `<script>(function(){
  // The sandbox is allow-scripts WITHOUT allow-same-origin (so a view can never
  // reach the app's real origin/storage), which makes any localStorage access
  // throw a SecurityError. Views commonly touch it (a per-browser id, a cache),
  // and an uncaught throw blanks the view. Provide an in-memory polyfill so that
  // never happens.
  //
  // IT FORGETS, AND THAT IS THE TRAP. A write here succeeds and is gone with the
  // frame, so a view that stores a preference in localStorage looks correct all
  // session and has lost it by the next open, with nothing reporting a fault.
  // Synced state belongs in mod.type.*; state that is PERSONAL and durable (a
  // reading position, a toggle) belongs in mod.prefs, which the host keeps in
  // its own real storage. See capabilities/prefs.ts.
  try {
    var _ls = {};
    var mem = {
      getItem: function(k){ return Object.prototype.hasOwnProperty.call(_ls, k) ? _ls[k] : null; },
      setItem: function(k, v){ _ls[k] = String(v); },
      removeItem: function(k){ delete _ls[k]; },
      clear: function(){ _ls = {}; },
      key: function(i){ return Object.keys(_ls)[i] || null; }
    };
    Object.defineProperty(mem, 'length', { get: function(){ return Object.keys(_ls).length; } });
    Object.defineProperty(window, 'localStorage', { configurable: true, get: function(){ return mem; } });
    Object.defineProperty(window, 'sessionStorage', { configurable: true, get: function(){ return mem; } });
  } catch(_){}
  var SCOPED = ${JSON.stringify(dataType)};
  var SURFACE = ${JSON.stringify(surface)};
  var INSTANCE = ${JSON.stringify(instanceId ?? null)};
  var SYNC = ${JSON.stringify(sync ?? null)};
  var seq = 0; var pending = {}; var subs = []; var surfaceSubs = []; var windowSubs = [];
  // RFC 0328: commands stay inside the mounted view. The host carries a name
  // and JSON in, and a JSON result out; it never learns what a Review tab or a
  // canvas node means. Registration is local to this frame and dies with it.
  var controlManifest = null; var controlHandler = null;
  // A command that arrives before the view has registered its commands (a
  // reload, a slow first render) waits for it, rather than being told the app
  // has none. Under the window's 5 s reply timeout, so the wait still ends in a
  // clear refusal if the view never registers.
  var controlWaiting = [];
  var CONTROL_WAIT_MS = 4000;
  function answerControl(d){
    var replyControl = function(result, error){
      parent.postMessage({ __modBridge:'control-result', id:d.id, result:result, error:error }, '*');
    };
    if (!controlManifest || typeof controlHandler !== 'function') {
      var waiting = { d: d, timer: setTimeout(function(){
        var i = controlWaiting.indexOf(waiting);
        if (i >= 0) controlWaiting.splice(i, 1);
        replyControl(undefined, 'this app does not declare view commands');
      }, CONTROL_WAIT_MS) };
      controlWaiting.push(waiting);
    } else if (!d.action) {
      replyControl(controlManifest);
    } else if (!controlManifest.actions || !Object.prototype.hasOwnProperty.call(controlManifest.actions, d.action)) {
      replyControl(undefined, 'this app does not declare the "' + String(d.action) + '" command');
    } else {
      Promise.resolve().then(function(){ return controlHandler(String(d.action), d.input && typeof d.input === 'object' ? d.input : {}); })
        .then(function(result){ replyControl(result); })
        .catch(function(err){ replyControl(undefined, (err && err.message) || String(err)); });
    }
  }
  // RFC 0304 3d: the host cannot see into this frame, so when a message is
  // sent it asks the frame to draw itself. The drawing library arrives as
  // TEXT with the first request, for the same reason the sync runtime does.
  var snapshotRuntime = null;
  function loadSnapshotRuntime(source){
    if (window.modernScreenshot) return Promise.resolve(window.modernScreenshot);
    if (snapshotRuntime) return snapshotRuntime;
    snapshotRuntime = new Promise(function(resolve, reject){
      if (!source) { reject(new Error('the host sent no snapshot runtime')); return; }
      var url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
      var el = document.createElement('script');
      el.onload = function(){ URL.revokeObjectURL(url); if (window.modernScreenshot) resolve(window.modernScreenshot); else reject(new Error('snapshot runtime registered nothing')); };
      el.onerror = function(){ URL.revokeObjectURL(url); snapshotRuntime = null; reject(new Error('snapshot runtime failed to load')); };
      el.src = url;
      document.head.appendChild(el);
    });
    return snapshotRuntime;
  }
  // A drawing clones elements, and a clone keeps attributes but not what the
  // person picked or typed, so copy that onto attributes and undo after.
  function pinFormState(){
    var undo = [];
    [].forEach.call(document.querySelectorAll('option'), function(o){
      var had = o.hasAttribute('selected');
      if (o.selected === had) return;
      o.toggleAttribute('selected', o.selected);
      undo.push(function(){ o.toggleAttribute('selected', had); });
    });
    [].forEach.call(document.querySelectorAll('input'), function(i){
      if (i.type === 'checkbox' || i.type === 'radio') {
        var hadC = i.hasAttribute('checked');
        if (i.checked === hadC) return;
        i.toggleAttribute('checked', i.checked);
        undo.push(function(){ i.toggleAttribute('checked', hadC); });
      } else if (i.getAttribute('value') !== i.value) {
        var hadV = i.getAttribute('value');
        i.setAttribute('value', i.value);
        undo.push(function(){ if (hadV === null) i.removeAttribute('value'); else i.setAttribute('value', hadV); });
      }
    });
    return function(){ undo.forEach(function(f){ f(); }); };
  }
  function frameBackground(){
    var clear = { '': 1, 'transparent': 1, 'rgba(0, 0, 0, 0)': 1 };
    var bg = document.body ? getComputedStyle(document.body).backgroundColor : '';
    if (clear[bg]) bg = getComputedStyle(document.documentElement).backgroundColor;
    return clear[bg] ? '#ffffff' : bg;
  }
  // Relay in-frame errors to the host (a sandboxed frame's console is opaque to
  // the parent), so a broken view surfaces instead of just going white.
  window.onerror = function(m, s, l, c, err){ try { parent.postMessage({ __modBridge:'error', message: (err && err.stack) || (m + ' @' + s + ':' + l) }, '*'); } catch(_){} };
  window.addEventListener('unhandledrejection', function(e){ try { parent.postMessage({ __modBridge:'error', message: 'unhandledrejection: ' + ((e.reason && (e.reason.stack || e.reason.message)) || String(e.reason)) }, '*'); } catch(_){} });
  // Window shortcuts (frameShortcuts.ts): a keydown here never reaches the
  // window, so post the chord up. Bubble phase and defaultPrevented, so a view
  // that binds the same chord keeps it. No backticks: this is the shim.
  var FORWARDED = ${JSON.stringify(FORWARDED_CODES)};
  var SHIFT_FORWARDED = ${JSON.stringify(SHIFT_FORWARDED_CODES)};
  var NATIVE_EDITING = ${JSON.stringify(NATIVE_EDITING_CODES)};
  window.addEventListener('keydown', function(e){
    if (e.defaultPrevented || !(e.metaKey || e.ctrlKey) || e.altKey) return;
    if (FORWARDED.indexOf(e.code) < 0) return;
    if (e.shiftKey && SHIFT_FORWARDED.indexOf(e.code) < 0) return;
    if (NATIVE_EDITING.indexOf(e.code) >= 0 && e.target && e.target.isContentEditable) return;
    e.preventDefault();
    try { parent.postMessage({ __modBridge:'key', code: e.code, key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey }, '*'); } catch(_){}
  });
  // Handshake: the host's message listener may attach a beat after this frame's
  // script runs, so a call posted immediately (a view's loadPolls on load) can
  // be lost, hanging the view. Queue every outbound message until the host
  // answers 'ready', and retry 'hello' until it does. Nothing is ever lost.
  var ready = false; var outbox = [];
  function post(msg){ if (ready) parent.postMessage(msg, '*'); else outbox.push(msg); }
  var hello = setInterval(function(){
    if (ready) { clearInterval(hello); } else parent.postMessage({ __modBridge:'hello' }, '*');
  }, 80);
  parent.postMessage({ __modBridge:'hello' }, '*');
  window.addEventListener('message', function(e){
    var d = e.data || {};
    if (d.__modBridge === 'ready') {
      if (d.context && typeof d.context === 'object') {
        surface.context = d.context;
        surfaceSubs.forEach(function(fn){ try { fn(); } catch(_){} });
      }
      if (!ready) { ready = true; clearInterval(hello); var q = outbox; outbox = []; q.forEach(function(m){ parent.postMessage(m, '*'); }); }
      // RFC 0275: an app that declared sync:'documents' is handed a MessagePort,
      // the guest runtime as TEXT, and the wasm as BYTES. Nothing here can be a
      // live object: this frame is a srcdoc document at an opaque origin, so it
      // cannot fetch, cannot resolve a bare specifier, and — measured, not
      // assumed — cannot even receive a compiled WebAssembly.Module, which is
      // silently dropped. Everything arrives as data and is built in this realm.
      if (d.sync === 'documents' && e.ports && e.ports[0] && !openReady) {
        openReady = bootSync(e.ports[0], d.runtime, d.wasm);
      }
    } else if (d.__modBridge === 'control') {
      answerControl(d);
    } else if (d.__modBridge === 'snapshot') {
      var replySnapshot = function(png, error){
        parent.postMessage({ __modBridge:'snapshot-result', id:d.id, png:png, error:error }, '*');
      };
      loadSnapshotRuntime(d.runtime).then(function(ms){
        return ms.createContext(document.documentElement, {
          width: innerWidth, height: innerHeight, scale: d.scale || 1,
          backgroundColor: frameBackground(), timeout: 3000,
          features: { restoreScrollPosition: true }
        }).then(function(ctx){
          // The library measures default styles in a helper frame it makes.
          // Inside this sandbox that frame is cross-origin to us, so it gets
          // none and skips the step.
          ctx.sandbox = { contentWindow: null };
          var unpin = pinFormState();
          return ms.domToPng(ctx).then(function(png){ unpin(); return png; }, function(err){ unpin(); throw err; });
        });
      }).then(function(png){ replySnapshot(png); }, function(err){ replySnapshot(undefined, (err && err.message) || String(err)); });
    } else if (d.__modBridge === 'result' && pending[d.id]) {
      var p = pending[d.id]; delete pending[d.id];
      if (d.error) p.reject(new Error(d.error)); else p.resolve(d.result);
    } else if (d.__modBridge === 'change') {
      subs.forEach(function(fn){ try { fn(); } catch(_){} });
    } else if (d.__modBridge === 'surface-context') {
      surface.context = d.context && typeof d.context === 'object' ? d.context : {};
      surfaceSubs.forEach(function(fn){ try { fn(); } catch(_){} });
    } else if (d.__modBridge === 'window-change') {
      windowSubs.forEach(function(fn){ try { fn(); } catch(_){} });
    }
  });
  function call(method, args){
    return new Promise(function(resolve, reject){
      var id = ++seq; pending[id] = { resolve: resolve, reject: reject };
      post({ __modBridge:'call', id:id, method:method, args:args });
    });
  }
  // ---- RFC 0275: real Automerge handles, in this realm ---------------------
  //
  // The handle NEVER crosses the boundary. The DOCUMENT syncs over the port and
  // this frame builds its own handle from its own repo, which is what makes
  // collaborative editing possible inside a sandbox at all.
  //
  // What that port serves was decided by the host's gate BEFORE this app could
  // ask, so open() resolving is itself the permission check: an ungranted id is
  // never served, and shows up here as a document that does not arrive.
  var openReady = null;
  var guest = null;
  function bootSync(port, runtimeSource, wasmBytes){
    return new Promise(function(resolve, reject){
      try {
        if (!runtimeSource || !wasmBytes) { reject(new Error('sync declared but the host sent no runtime')); return; }
        // Blob + <script>, in OUR realm. The runtime is an IIFE that hangs
        // itself on window rather than an ES module, because a module would
        // need an import map this frame has no way to resolve.
        var url = URL.createObjectURL(new Blob([runtimeSource], { type: 'text/javascript' }));
        var el = document.createElement('script');
        el.onload = function(){
          URL.revokeObjectURL(url);
          if (!window.__modGuestRuntime) { reject(new Error('guest runtime loaded but registered nothing')); return; }
          window.__modGuestRuntime.start(port, wasmBytes).then(function(api){ guest = api; resolve(api); }, reject);
        };
        el.onerror = function(){ URL.revokeObjectURL(url); reject(new Error('guest runtime failed to load')); };
        el.src = url;
        document.head.appendChild(el);
      } catch (err) { reject(err); }
    });
  }

  // Type-first facade; the host enforces the scoped type regardless of what is
  // passed, so all of these reach the same app's instances.
  var type = {
    list:   function(){ return call('list', []); },
    // create(data) or the legacy create(type, data). RFC 0338 3e adds
    // create(data, { folder }): an object first means the new form, so the
    // legacy string-first call reads exactly as it did.
    create: function(){ var a=[].slice.call(arguments); if (a.length >= 2 && a[0] && typeof a[0] === 'object') return call('create', [a[0], a[1] || null]); var data=a[a.length-1]; return call('create', [data]); },
    // RFC 0267 contract: read(id), change(id, patch) — a PARTIAL patch (fields
    // omitted are preserved). update(id, data) is a whole-value replace kept as
    // an explicit escape hatch. get is an alias of read.
    read:   function(){ var a=[].slice.call(arguments); return call('read', [a[a.length-1]]); },
    get:    function(){ var a=[].slice.call(arguments); return call('read', [a[a.length-1]]); },
    change: function(){ var a=[].slice.call(arguments); return call('change', [a[a.length-2], a[a.length-1]]); },
    update: function(){ var a=[].slice.call(arguments); return call('update', [a[a.length-2], a[a.length-1]]); },
    // The instance's CATALOG title, which is a different thing from its data.
    //
    // projectDocuments reads the catalog and nothing else (title ?? path ?? id)
    // deliberately, so listing a project never hydrates an instance doc. That
    // zero-hydration read is why an app cannot just name a field and have the
    // sidebar show it: the sidebar never opens the record. So a human-readable
    // name has to be WRITTEN to the catalog, and this is how an app does that
    // for its own instances. Same requireOwnInstance scoping as change/update,
    // so it cannot retitle another app's records.
    //
    // NOTE: this comment lives inside the shim's template literal. No backticks
    // here, ever - one closes the string and the file stops parsing.
    rename: function(){ var a=[].slice.call(arguments); return call('rename', [a[a.length-2], a[a.length-1]]); },
    // Open one of this app's OWN records as a page of its own: its own pane,
    // its own place in the url, its own row in a session's holdings. Scoped to
    // the app's type, so it needs no documents grant.
    open: function(){ var a=[].slice.call(arguments); return call('open', [a[a.length-1]]); },
    // Unlink an instance from the project catalog. The record document is left
    // to reachability GC, so anything anchored to it (comments live on the log,
    // not on the instance) survives the row disappearing. Scoped by
    // requireOwnInstance like every other write here, so an app can only drop
    // its own type's records. remove is the alias, matching mod.comments.
    delete: function(){ var a=[].slice.call(arguments); return call('delete', [a[a.length-1]]); },
    remove: function(){ var a=[].slice.call(arguments); return call('delete', [a[a.length-1]]); },
    // RFC 0271: an instance's BODY, alongside its fields.
    //
    // A review is a plan that grew a branch, so the plan is the review's own
    // prose rather than a second document to name and keep in step. Routed
    // through mod.type rather than mod.docs deliberately: docs EXCLUDES
    // instances so an app holding the documents grant cannot write another
    // app's records, and relaxing that guard to reach your own body would give
    // the boundary away. Same requireOwnInstance scoping as change/update.
    readBody:  function(){ var a=[].slice.call(arguments); return call('readBody', [a[a.length-1]]); },
    writeBody: function(){ var a=[].slice.call(arguments); return call('writeBody', [a[a.length-2], a[a.length-1]]); },
    onChange: function(fn){ subs.push(fn); return function(){ subs = subs.filter(function(x){return x!==fn;}); }; }
  };
  // Cross-interface READS (RFC 0267 §4a), granted per the app's capabilities.
  // An undeclared namespace still exists on window.mod but the host rejects the
  // call, so a view discovers the missing grant as a rejected promise.
  var docs = {
    list: function(){ return call('docs.list', []); },
    read: function(){ var a=[].slice.call(arguments); return call('docs.read', [a[a.length-1]]); },
    // WRITES need the "documents" write grant, separately from the read grant:
    // an app that reads the project's documents is not thereby allowed to
    // rewrite them. Same default-deny shape as comments.
    // Create a document and get its id back. Needs the "documents" WRITE grant,
    // like write/patch: an app that can only read must not mint documents.
    create:  function(path, content){ return call('docs.create', [path, content]); },
    // Host NAVIGATION, not a data call: open this document on the canvas, the
    // same way sessions.open hands a session to the real thread UI. Lets a view
    // hand a plan to the host's own document surface rather than embedding an
    // editor it cannot host inside a sandboxed frame.
    open:    function(id){ return call('docs.open', [id]); },
    write: function(id, content){ return call('docs.write', [id, content]); },
    // Exact-string replacement, so an app changing part of a document does not
    // have to reproduce the whole thing. Still read-modify-write under the
    // hood: concurrent edits are last-write-wins until DocumentDoc.content
    // becomes an Automerge text field (RFC 0263 slice 5).
    patch: function(id, oldString, newString){ return call('docs.patch', [id, oldString, newString]); }
  };
  var sessions = {
    list: function(){ return call('sessions.list', []); },
    read: function(){ var a=[].slice.call(arguments); return call('sessions.read', [a[a.length-1]]); },
    // Host NAVIGATION (not a data read): ask the shell to open this session in its
    // agent panel. Lets a review view hand a session off to the real thread UI
    // instead of inlining a transcript. Needs the "sessions" grant.
    open: function(id){ return call('sessions.open', [id]); },
    // Say something to a session, as a person would: the text lands as a user
    // message on its thread and starts a turn. Needs the "sessions" WRITE
    // grant, which is separate from the read grant that lists them.
    send: function(id, text){ return call('sessions.send', [id, text]); }
  };
  // Comments (RFC 0267 §4a): the shared annotation interface, a WRITE capability.
  // create/resolve need the app's "comments" write grant; list needs read OR write.
  var comments = {
    list:    function(target){ return call('comments.list', [target || {}]); },
    // The same filter as list, grouped into threads: { root, replies } with
    // replies oldest-first (RFC 0289 §4). A view that renders an exchange wants
    // this; a view that counts still wants list.
    threads: function(target){ return call('comments.threads', [target || {}]); },
    // mentions (RFC 0292) is OPTIONAL and third: an app written before this
    // passes two arguments and behaves exactly as it did. Each entry is
    // { kind, id, at, len } - the offsets of the chip inside the body, so the
    // prose stays readable to anything that ignores them. NO BACKTICKS in this
    // comment: it lives inside the shim's template literal.
    create:  function(target, body, mentions){ return call('comments.create', [target, body, mentions || []]); },
    // reply(id, body): answer an existing comment. NO target argument - a reply
    // inherits its root's anchor, so an app cannot put an answer on a different
    // line from the question. id may be the root or one of its replies; both
    // land in the same thread (one level, RFC 0289 §3). Resolves to
    // { id, notifiedSessionId }: when an agent wrote the comment being answered,
    // its session is woken to answer back.
    reply:   function(id, body, mentions){ return call('comments.reply', [id, body, mentions || []]); },
    // resolve(id) marks it done; resolve(id, false) puts it back. The flag has
    // always existed on the core mutation and was simply never passed, which
    // made resolving a one-way door in every app that used this bridge.
    resolve: function(id, resolved){ return call('comments.resolve', [id, resolved !== false]); },
    // RFC 0324 3c, 3d: a CommentPort's optional reads, served by the window.
    reader:  function(target, replyTo){ return call('comments.reader', [target || {}, replyTo || '']); },
    pending: function(rootId){ return call('comments.pending', [rootId]); },
    // Deleting is NOT resolving. Resolve says "dealt with" and keeps the text
    // on the line; delete says "this should not exist" and removes it. A
    // comment typed on the wrong line needs the second one, and resolve is no
    // substitute because the comment stays anchored to code it was never about.
    remove:  function(id){ return call('comments.delete', [id]); }
  };
  // This view's own durable, PRIVATE state: a fold, a toggle, a last-used tab.
  // The host keeps it in real storage on this app's behalf, namespaced by the
  // app's dataType, because the sandbox makes this frame's own localStorage a
  // polyfill that forgets. Not synced and not shared - a reading position is
  // one person's, and putting it on the record would move somebody else's view.
  // get resolves null when nothing was ever set, which is where a default goes.
  // (No backticks in this comment - it is inside the shim template literal.)
  var prefs = {
    get:    function(key){ return call('prefs.get', [key]); },
    set:    function(key, value){ return call('prefs.set', [key, value]); },
    remove: function(key){ return call('prefs.remove', [key]); }
  };
  var tasks = {
    list: function(opts){ return call('tasks.list', [opts || {}]); },
    get: function(id){ return call('tasks.get', [id]); },
    create: function(input){ return call('tasks.create', [input || {}]); },
    update: function(id, patch){ return call('tasks.update', [id, patch || {}]); },
    complete: function(id, note, refs){ return call('tasks.complete', [id, note || '', refs || []]); },
    // RFC 0319 3g: evidence, not status. recordEvent writes what happened;
    // importChecked is for a line that arrives already ticked, which is
    // implementation and not a merge.
    recordEvent: function(id, event){ return call('tasks.recordEvent', [id, event || {}]); },
    importChecked: function(id, note){ return call('tasks.importChecked', [id, note || '']); }
  };
  // RFC 0325: the host and the frame execute the SAME element installer. The
  // app-facing import resolves to these elements; the built page keeps no
  // frozen copy of their implementation. The bridge is the frame's port.
  // esbuild names nested classes/functions in a function's toString() output
  // with this local helper. Include it because the installer crosses realms as
  // source, without the outer bundle's helpers.
  var __name = function(target, value){
    try { Object.defineProperty(target, 'name', { value: value, configurable: true }); } catch(_){}
    return target;
  };
  var taskElements = (${installTaskElements.toString()})(window);
  // RFC 0324 S7: the comment row, the same element the window draws with.
  (${installCommentElements.toString()})(window);
  taskElements.setDefaultPort({
    get: tasks.get,
    update: tasks.update,
    subscribe: type.onChange
  });
  // VCS (RFC 0264 §5): reads served by the git server over HTTP (a plain
  // request/response; nothing persisted, nothing on the relay). The base to diff
  // against is selectable so a review compares against any ref, not just the
  // project trunk. Gated by the "vcs" read grant.
  var vcs = {
    sources: function(){ return call('vcs.sources', []); },
    branches: function(base){ return call('vcs.branches', [base]); },
    diff:     function(branch, base){ return call('vcs.diff', [branch, base]); },
    // The UNCOMMITTED tree against HEAD, including untracked files. Returns the
    // same FileDiff[] as diff, so a view renders it with no special case. It
    // takes no ref and has no verdict: the tree changes as you type, so nothing
    // durable can be anchored to it.
    working:  function(){ return call('vcs.working', []); },
    // The commits a branch introduced, newest first, with per-commit counts.
    // Two-dot against the base: these are the commits that are the branch's
    // own, which is a different question from what its tree adds.
    commits:  function(branch, base, limit){ return call('vcs.commits', [branch, base, limit]); },
    // One commit's diff, against its first parent. Same FileDiff[] as diff, so
    // the same renderer draws it.
    commitDiff: function(sha){ return call('vcs.commitDiff', [sha]); },
    // One file as it is ON DISK. fileAt reads a committed blob, so it cannot
    // answer for the uncommitted side; a renderer needs whole files, not hunks.
    workingFile: function(path){ return call('vcs.workingFile', [path]); },
    fileAt:   function(ref, path){ return call('vcs.fileAt', [ref, path]); },
    status:   function(branch, base){ return call('vcs.status', [branch, base]); },
    // Pull requests. "pr" is a read (does this branch have one, can it merge);
    // openPr and mergePr are the interface's only WRITES and take the separate
    // "vcs" write grant. They are delegated to the gh CLI the user has already
    // authenticated, so the host holds no GitHub credentials of its own.
    // (No backticks in this comment - it is inside the shim template literal.)
    pr:       function(branch){ return call('vcs.pr', [branch]); },
    prs:      function(opts){ return call('vcs.prs', [opts || {}]); },
    openPr:   function(opts){ return call('vcs.openPr', [opts || {}]); },
    mergePr:  function(opts){ return call('vcs.mergePr', [opts || {}]); }
  };
  // Bind the same interface to a named source. The host consumes this selector
  // before interpreting method arguments, so older positional calls still use
  // the project's default source, including zero-argument calls like working.
  vcs.forSource = function(sourceId){
    var scoped = {};
    Object.keys(vcs).forEach(function(name){
      if (name === 'forSource' || name === 'sources') return;
      scoped[name] = function(){
        return call('vcs.' + name, [{ sourceId: sourceId }].concat(Array.prototype.slice.call(arguments)));
      };
    });
    return scoped;
  };
  // Reviews are the review app's own dataType (RFC 0267): a review is a typed
  // instance created via mod.type.*, not a core mod.reviews interface. So there is
  // no reviews namespace here — the review view uses mod.type like any app.
  // Surface (RFC 0273): tell the host WHAT THIS VIEW IS SHOWING. Not a read and
  // not navigation, but the host cannot infer it: opening an app opens its VIEW
  // document, so file.id is app source code, while the thing on screen is an
  // instance the app chose. Without this the canvas side column scopes comments
  // to the view doc and is empty forever on any app. Pass null to clear.
  var surface = {
    // WHERE this view is rendering (RFC 0273): 'canvas' for the main column,
    // 'panel' for the side column. The same view document is mounted in both,
    // so a view that declares a panel section branches on this rather than
    // shipping a second document.
    name: SURFACE,
    context: {},
    onChange: function(fn){ surfaceSubs.push(fn); return function(){ surfaceSubs = surfaceSubs.filter(function(x){return x!==fn;}); }; },
    // setSubject(id) scopes the host's column to one document. The optional
    // second argument WIDENS the comment query without changing what the
    // surface is: setSubject(reviewId, { include: [planDocId] }) aggregates a
    // review's comments with its plan's. The app passes ids it already holds;
    // the host validates each and returns nothing, so this grants no access.
    setSubject: function(id, opts){
      return call('surface.setSubject', [id === undefined ? null : id, opts || null]);
    },
    // Ask the host to put ITS composer in this pane's footer, so a person can
    // talk to the session about what they are looking at without opening a
    // second pane. The host owns which session that is; this only asks for the
    // control. Pass false to hand it back.
    setComposer: function(on){
      return call('surface.setComposer', [on !== false]);
    },
    // Say what is selected INSIDE the subject, so the composer can show it and
    // the agent knows what "this" refers to. { label, ref }; null clears.
    // Call it whenever the selection changes, including to null.
    setSelection: function(sel){
      return call('surface.setSelection', [sel || null]);
    },
    // An app declares the commands its CURRENT mounted view can answer. This is
    // not an AppDoc capability or synced state: another build may expose a
    // different view, and an unmounted build can answer nothing.
    control: {
      register: function(manifest, handler){
        if (!manifest || typeof manifest !== 'object' || !manifest.actions || typeof manifest.actions !== 'object') {
          throw new Error('a control manifest with actions is required');
        }
        if (typeof handler !== 'function') throw new Error('a control handler is required');
        controlManifest = manifest; controlHandler = handler;
        var waited = controlWaiting; controlWaiting = [];
        waited.forEach(function(w){ clearTimeout(w.timer); answerControl(w.d); });
        return function(){
          if (controlHandler === handler) { controlManifest = null; controlHandler = null; }
        };
      }
    }
  };
  var windowApi = {
    read: function(){ return call('window.read', []); },
    open: function(subject, where){ return call('window.open', [subject, where]); },
    close: function(leafId){ return call('window.close', [leafId]); },
    focus: function(leafId){ return call('window.focus', [leafId]); },
    setSpace: function(spaceId){ return call('window.setSpace', [spaceId]); },
    onChange: function(fn){ windowSubs.push(fn); return function(){ windowSubs = windowSubs.filter(function(x){return x!==fn;}); }; }
  };
  // RFC 0271: WHICH RECORD this view is showing, when it is showing one.
  //
  // The inverse of surface.setSubject: that is the view telling the host, this
  // is the host telling the view. Read-only and set at boot, because it is the
  // identity of the thing on screen and a view that could change it could
  // reassign what the host thinks it is looking at.
  //
  // null when the app was opened by name rather than as a record. A view that
  // ignores it behaves exactly as before, which is what makes this additive:
  // an app that never reads instanceId keeps working unchanged.
  // RFC 0275. Present only when the app declared sync:'documents' -- absent is
  // how an app that did not opt in learns it did not, rather than getting a
  // function that fails later for a reason it cannot see.
  function open(documentId){
    if (!openReady) return Promise.reject(new Error('this app did not declare sync: "documents", so it has no repo'));
    return openReady.then(function(api){ return api.open(documentId); });
  }
  // RFC 0292: what this app can NAME from where somebody is typing. A function
  // rather than a namespace, because there is one question to ask; the host
  // answers with the same projection its own composers use, filtered to the
  // kinds this app is granted.
  function candidates(prefix, opts){ return call('candidates.list', [prefix || '', opts || {}]); }
  // RFC 0336 S2: what ⌘K and the agent search, every project in the World,
  // each row saying which project it is in. A read under the "world" grant;
  // open is navigation to a row it found, never a write.
  var world = {
    search: function(text, opts){ return call('world.search', [text || '', opts || {}]); },
    open:   function(id){ return call('world.open', [id]); }
  };
  window.mod = { type: type, docs: docs, sessions: sessions, comments: comments, tasks: tasks, candidates: candidates, world: world, vcs: vcs, prefs: prefs, surface: surface, window: windowApi, instanceId: INSTANCE, onChange: type.onChange, dataType: SCOPED };
  if (SYNC === 'documents') window.mod.open = open;
})();</script><script type="importmap">${importMap}</script>`
}

/** Prepend the shim to an HTML document so `window.mod` exists before the
 *  view's own script runs. Injected right after <head> (or at the top if none),
 *  so it is the first script the frame evaluates. */
export function injectBridge(
  html: string,
  dataType: string,
  surface = 'canvas',
  instanceId?: string,
  sync?: 'documents',
): string {
  const shim = bridgeShim(dataType, surface, instanceId, sync)
  const headMatch = html.match(/<head[^>]*>/i)
  if (headMatch) {
    const at = headMatch.index! + headMatch[0].length
    return html.slice(0, at) + shim + html.slice(at)
  }
  return shim + html
}

/**
 * Capability handlers, keyed by namespace (RFC 0264 §4). The bridge is generic
 * dispatch: a namespaced call (`vcs.*`) goes to the capability that
 * owns it, which does its own grant enforcement, so a capability's logic lives in
 * its module, not in core `appBridge`. Core interfaces (docs/sessions/comments)
 * and the app's own type stay inline in `handleBridgeCall` below. Adding a
 * capability = one entry here (the static form of "installing" it).
 */
const CAPABILITY_HANDLERS: Record<string, BridgeHandler> = {
  vcs: vcsHandler,
  // A view's own durable scratch space. Ungated on purpose: it reaches nothing
  // but itself, and it exists because the sandbox makes localStorage a lie.
  prefs: prefsHandler,
}

/**
 * What a view says it has selected inside its subject.
 *
 * Deliberately two strings and nothing else. The host cannot know what a
 * selection MEANS in an app it did not write — a canvas layer, a diff hunk, a
 * row — so it carries the app's own words for it rather than a shape it would
 * have to understand, and hands both to the agent as text.
 */
export interface SurfaceSelection {
  /** The app's own name for the thing, shown to a person on the composer. */
  label: string
  /** An opaque app-scoped handle, passed through to the agent verbatim. */
  ref?: string
}

/**
 * Run one bridge call against the repo, scoped to `dataType`. Returns the value
 * the shim resolves with. Every write goes through mod-core, so it persists and
 * syncs; every read is a live projection.
 */
export async function handleBridgeCall(
  repo: Repo,
  projectId: string,
  dataType: string,
  method: string,
  args: unknown[],
  grants: string[] = [],
  writeGrants: string[] = [],
  /** Who writes a comment. Absent means the person at this window, asked
   *  for once (RFC 0324 3a). Tests pass one explicitly. */
  author?: string,
  nav: {
    openSession?: (id: string) => void
    setSubject?: (id: string | null, include?: string[]) => void
    openDocument?: (id: string) => void
    /** Put the host's OWN composer in this pane, or take it away. Not a
     *  composer of the app's own: see `surface.setComposer` in the shim. */
    setComposer?: (on: boolean) => void
    /** What the view has selected INSIDE its subject, so the composer can say
     *  what "this" means. Null clears. */
    setSelection?: (selection: SurfaceSelection | null) => void
    /** Deliver a root comment that names nobody to the space's session (RFC
     *  0301 §3d). The window's, because only it knows the space; the core
     *  cannot. Resolves to the session that has it, or null. */
    /** The space's session, for saying who a reply reaches and what a
     *  thread waits on (RFC 0324 3c, 3d). The window's, like routeComment. */
    space?: () => { sessionId: string | null; canStart: boolean }
    routeComment?: (comment: {
      id: string
      body: string
      documentId?: string
    }) => Promise<string | null>
  } = {},
): Promise<unknown> {
  const ws = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (ws as { whenReady?: () => Promise<void> }).whenReady?.()

  // Generic capability dispatch (RFC 0264 §4): a namespaced call whose namespace
  // is registered (vcs.*) goes to that capability's handler, which does
  // its own grant enforcement. Core interfaces (comments/docs/sessions) and the
  // app's own type (no namespace) fall through to the inline handling below.
  const dot = method.indexOf('.')
  const namespace = dot > 0 ? method.slice(0, dot) : ''
  const handler = CAPABILITY_HANDLERS[namespace]
  if (handler) {
    return handler(method, args, {
      repo,
      projectId,
      ws,
      dataType,
      grants,
      writeGrants,
      author: author ?? ANONYMOUS_AUTHOR,
      nav,
    })
  }

  // Comments (RFC 0267 §4a) are a WRITE interface: create/resolve require the
  // app's "comments" write grant; list is a read, so a read OR write grant
  // serves it. Default-DENY like the read namespaces. A comment created here is
  // attributed to the current user (the human operating the view).
  // RFC 0273: the view declares its subject. Ungated by design, because it
  // grants nothing: the app hands over an id it already has and receives
  // nothing back. It is still VALIDATED against this project's documents, so
  // a view cannot point the host's column at a document outside the project.
  // A bad id clears rather than throws: a display hint must never break a view.
  if (method === 'surface.setSubject') {
    const known = new Set(projectDocuments(ws.doc()).map((d) => d.id))
    const raw = args[0]
    const id = raw == null ? null : String(raw)
    const subject = id !== null && known.has(id) ? id : null

    // `include` widens the comment query to documents the app says belong to
    // this surface (a review's plan, say). Each is validated exactly as the
    // subject is, and unknown ids are dropped rather than throwing, because a
    // scoping hint must never break a view. Deduped against the subject so a
    // caller passing it twice does not double every comment.
    const opts = (args[1] ?? {}) as { include?: unknown }
    const include = (Array.isArray(opts.include) ? opts.include : [])
      .map((v) => String(v))
      .filter((v) => known.has(v) && v !== subject)
    const uniqueInclude = [...new Set(include)]

    nav.setSubject?.(subject, uniqueInclude)
    return { subject, include: uniqueInclude }
  }

  /**
   * Ask for the host's composer, in this pane (RFC 0266 §3e).
   *
   * NOT a composer of the app's own, and that is the whole point. The host
   * keeps ONE Composer with one draft, one model choice and one send path, and
   * portals it into a slot; a second one built inside an app would be a second
   * draft that evaporates, which is the exact bug the slot was introduced to
   * fix. So the app asks, and the host lends it the real thing.
   *
   * A HINT, like setSubject: it grants nothing and reads nothing. The session
   * it sends to is the host's business, never the app's — an app that could
   * choose the session could put words in any conversation in the project.
   */
  if (method === 'surface.setComposer') {
    const on = args[0] !== false
    nav.setComposer?.(on)
    return { composer: on }
  }

  /**
   * What the view has SELECTED inside its subject.
   *
   * `setSubject` says which record is on screen; this says which part of it a
   * person is pointing at, which is what the composer needs to make "make this
   * one dashed" mean something. The label is the app's own words for the thing
   * and is shown to a person, so it is TRUNCATED rather than trusted: a view
   * that returned a kilobyte would otherwise push the composer off the screen.
   *
   * `ref` is an opaque app-scoped handle (a canvas node id, say). The host
   * never resolves it and never validates it against anything, because it
   * names something INSIDE a document rather than a document — so it is passed
   * back to the agent as text and grants nothing on its own.
   */
  if (method === 'surface.setSelection') {
    const raw = args[0]
    if (raw == null || typeof raw !== 'object') {
      nav.setSelection?.(null)
      return { selection: null }
    }
    const sel = raw as { label?: unknown; ref?: unknown }
    const label = String(sel.label ?? '')
      .trim()
      .slice(0, 200)
    if (!label) {
      nav.setSelection?.(null)
      return { selection: null }
    }
    const selection: SurfaceSelection = {
      label,
      ...(typeof sel.ref === 'string' && sel.ref
        ? { ref: sel.ref.slice(0, 200) }
        : {}),
    }
    nav.setSelection?.(selection)
    return { selection }
  }

  // Tasks (RFC 0232) are a core interface, like comments: no capability doc to
  // install, just a grant-gated view of state the project already holds. This
  // is what the installed Tasks APP reads and writes through.
  if (method.startsWith('tasks.')) {
    const canWrite = writeGrants.includes('tasks')
    return handleTasksCall(method, args, {
      repo,
      projectId,
      canRead: canWrite || grants.includes('tasks'),
      canWrite,
    })
  }

  if (method === 'candidates.list') {
    // RFC 0292 §3c — the same ranked answer the host's own composers get, so a
    // sandboxed frame with no project handle offers the same names for the
    // same letters. This is the test of whether §3c was factored right: nothing
    // here re-implements ranking, it forwards a projection.
    const kinds = mentionableKinds(grants, writeGrants)
    if (kinds.length === 0) {
      throw new Error(
        'this app is not granted "sessions" or "documents", so it can name nothing',
      )
    }
    const prefix = String(args[0] ?? '')
    const opts =
      args[1] && typeof args[1] === 'object'
        ? (args[1] as { kinds?: unknown; limit?: number })
        : {}
    // The app's own kind filter INTERSECTS the grant, never widens it.
    const asked = Array.isArray(opts.kinds)
      ? (opts.kinds as MentionKind[]).filter((k) => kinds.includes(k))
      : kinds
    if (asked.length === 0) return [] as Candidate[]
    return projectCandidates(ws.doc(), prefix, {
      kinds: asked,
      ...(typeof opts.limit === 'number' ? { limit: opts.limit } : {}),
    })
  }

  if (method.startsWith('world.')) {
    // RFC 0336 S2 for an app: the rows ⌘K and the agent's `search` read, every
    // project the World lists. Its own grant, because it reaches past the
    // project the app was installed into; a "documents" grant does not imply
    // it. Read-only: search reads catalogs, open is navigation.
    if (!grants.includes('world')) {
      throw new Error('this app is not granted "world" access')
    }
    const sources = await worldCatalogs(repo, readWorldPointer(), projectId)
    if (method === 'world.search') {
      const opts =
        args[1] && typeof args[1] === 'object'
          ? (args[1] as { kinds?: unknown; limit?: number })
          : {}
      const kinds = Array.isArray(opts.kinds)
        ? (opts.kinds as string[])
        : ['document', 'session']
      const limit = typeof opts.limit === 'number' ? opts.limit : 20
      // Over-read, then filter: searchCatalogs takes one kind, and an app
      // that never asked for tasks should not have them fill its page.
      const { results } = searchCatalogs(sources, {
        text: String(args[0] ?? ''),
        limit: 100,
      })
      return results
        .filter((r) => kinds.includes(r.kind))
        .slice(0, limit)
        .map((r) => ({
          id: r.id,
          kind: r.kind,
          label: r.title,
          project: r.project,
          projectId: r.projectId,
          ...(r.hint ? { hint: r.hint } : {}),
        }))
    }
    if (method === 'world.open') {
      const id = String(args[0] ?? '')
      // Only a row a search could have returned: an id no project in the
      // World lists does not open, the same refusal docs.open makes.
      const row = sources.flatMap(catalogRows).find((r) => r.id === id)
      if (!id || !row) throw new Error('nothing in this World has that id')
      if (row.kind === 'session') nav.openSession?.(id)
      else if (row.kind === 'document') nav.openDocument?.(id)
      else throw new Error(`a ${row.kind} does not open from here`)
      return { opened: id, projectId: row.projectId }
    }
    throw new Error(`unknown method: ${method}`)
  }

  if (method.startsWith('comments.')) {
    const canWrite = writeGrants.includes('comments')
    const canRead = canWrite || grants.includes('comments')
    switch (method) {
      case 'comments.list': {
        if (!canRead) {
          throw new Error('this app is not granted "comments" access')
        }
        const target =
          args[0] && typeof args[0] === 'object'
            ? (args[0] as Record<string, string | undefined>)
            : {}
        // Each with its writer resolved (RFC 0324 3a): a frame cannot read
        // the project's people, so the face travels with the comment. Read
        // where they live (RFC 0339).
        const doc = ws.doc()
        return projectComments(
          await loadComments(repo, projectId, target),
          target,
        ).map((c) => ({
          ...c,
          authorInfo: sessionFace(projectAuthor(doc, c)),
        }))
      }
      case 'comments.create': {
        if (!canWrite) {
          throw new Error('this app is not granted "comments" write access')
        }
        const target =
          args[0] && typeof args[0] === 'object'
            ? (args[0] as Record<string, string>)
            : {}
        const body = String(args[1] ?? '')
        // RFC 0292: what the body names, filtered to the kinds this app may
        // resolve. An app that cannot LIST sessions must not be able to wake one
        // by naming an id it guessed.
        const mentions = allowedMentions(args[2], grants, writeGrants)
        const who = author ?? (await commentAuthor(repo, projectId))
        const id = await createComment(repo, projectId, {
          target,
          author: who,
          body,
          ...(mentions.length > 0 ? { mentions } : {}),
        })
        if (mentions.length === 0) {
          // RFC 0301 §3d: naming nobody sends it to the space's session.
          const answeredBy = nav.routeComment
            ? await nav.routeComment({
                id,
                body,
                ...(target.documentId ? { documentId: target.documentId } : {}),
              })
            : null
          return answeredBy ? { id, answeredBy } : { id }
        }
        const delivered = await deliverMentions(repo, projectId, {
          commentId: id,
          mentions,
          author: who,
          body,
          ...(target.documentId ? { documentId: target.documentId } : {}),
          ...(target.anchorText ? { anchorText: target.anchorText } : {}),
          // The app's own anchor, carried into the wake. The canvas has sent
          // `nodeId` since it grew comments; nothing downstream forwarded it, so
          // a session named from a canvas node was told only which document, and
          // had to guess which of its nodes "this" meant.
          ...(target.nodeId ? { nodeId: target.nodeId } : {}),
        })
        return { id, mentioned: delivered.delivered.map((d) => d.sessionId) }
      }
      case 'comments.threads': {
        // RFC 0289 §4. The grouped read sits BESIDE the flat one rather than
        // replacing it: a count and a filter want every comment, a column wants
        // exchanges, and both get asked.
        if (!canRead) {
          throw new Error('this app is not granted "comments" access')
        }
        const target =
          args[0] && typeof args[0] === 'object'
            ? (args[0] as Record<string, string | undefined>)
            : {}
        const doc = ws.doc()
        const withAuthor = <C extends CommentEntry>(c: C) => ({
          ...c,
          authorInfo: sessionFace(projectAuthor(doc, c)),
        })
        return projectThreads(
          await loadComments(repo, projectId, target),
          target,
        ).map((t) => ({
          root: withAuthor(t.root),
          replies: t.replies.map(withAuthor),
        }))
      }
      case 'comments.reader': {
        // RFC 0324 3c: who a reply to this thread would reach, said before it
        // is sent. A read, so the read grant is enough.
        if (!canRead) {
          throw new Error('this app is not granted "comments" access')
        }
        const replyTo = String(args[1] ?? '')
        const home = replyTo
          ? await locateComment(repo, projectId, replyTo)
          : null
        return commentReader({
          projectDoc: ws.doc(),
          ...(home ? { comments: home.handle.doc() } : {}),
          mentions: [],
          ...(replyTo ? { replyTo } : {}),
          space: nav.space?.() ?? { sessionId: null, canStart: false },
        })
      }
      case 'comments.pending': {
        // RFC 0324 3d: what this thread is waiting for, off the registry.
        if (!canRead) {
          throw new Error('this app is not granted "comments" access')
        }
        const rootId = String(args[0] ?? '')
        const doc = ws.doc()
        // The thread where it lives (RFC 0339), not the whole project.
        const home = await locateComment(repo, projectId, rootId)
        if (!home) return null
        const map = home.handle.doc()
        const thread = projectThreads(map, {}).find((t) => t.root.id === rootId)
        if (!thread) return null
        return threadWaiting({
          projectDoc: doc,
          comments: map,
          thread,
          spaceSessionId: nav.space?.().sessionId ?? null,
        })
      }
      case 'comments.reply': {
        if (!canWrite) {
          throw new Error('this app is not granted "comments" write access')
        }
        const parentId = String(args[0] ?? '').trim()
        if (!parentId) throw new Error('comments.reply needs a comment id')
        const body = String(args[1] ?? '')
        // answerComment, not createComment and not the bare replyToComment
        // mutation: this is where a person answers an agent, and it wakes the
        // session that wrote the comment being answered (RFC 0289). Written to
        // either of the other two the reply would land correctly and reach
        // nobody, which is the failure this whole path exists to remove.
        //
        // No target argument: a reply takes its root's, so the host cannot let
        // an app put an answer on a different line from the question.
        const mentions = allowedMentions(args[2], grants, writeGrants)
        const who = author ?? (await commentAuthor(repo, projectId))
        const out = await answerComment(repo, projectId, {
          parentId,
          author: who,
          body,
          ...(mentions.length > 0 ? { mentions } : {}),
        })
        if (mentions.length === 0) {
          return { id: out.id, notifiedSessionId: out.notifiedSessionId }
        }
        // Two edges, as everywhere else: the reply wakes whoever wrote the
        // comment, the mentions wake whoever the reply names.
        const delivered = await deliverMentions(repo, projectId, {
          commentId: out.id,
          mentions,
          author: who,
          body,
        })
        return {
          id: out.id,
          notifiedSessionId: out.notifiedSessionId,
          mentioned: delivered.delivered.map((d) => d.sessionId),
        }
      }
      case 'comments.resolve': {
        if (!canWrite) {
          throw new Error('this app is not granted "comments" write access')
        }
        const id = String(args[0] ?? '')
        // Defaults to true, so a caller that passes only an id behaves exactly
        // as before; `false` un-resolves. Acts on the whole THREAD whichever id
        // in it is named (RFC 0289 §3).
        const resolved = args[1] !== false
        await resolveComment(repo, projectId, id, resolved)
        return { id, resolved }
      }
      case 'comments.delete': {
        if (!canWrite) {
          throw new Error('this app is not granted "comments" write access')
        }
        const id = String(args[0] ?? '')
        await deleteComment(repo, projectId, id)
        return { id }
      }
      default:
        throw new Error(`unknown method: ${method}`)
    }
  }

  // Cross-interface reads are default-DENY: only namespaces the app declared in
  // its capabilities.reads are served; an ungranted namespace rejects, so a
  // hostile or over-reaching view can only see what its catalog entry admits.
  if (method.startsWith('docs.') || method.startsWith('sessions.')) {
    const namespace = method.startsWith('docs.') ? 'documents' : 'sessions'
    const isDocWrite =
      method === 'docs.write' ||
      method === 'docs.patch' ||
      method === 'docs.create'
    /**
     * Saying something to a session is a WRITE, and the biggest one an app can
     * make: it puts words in a conversation as though a person typed them, and
     * starts a turn that will act on them. So it is gated on the `sessions`
     * WRITE grant, which is separate from the read grant an app needs merely to
     * list them — an app that can see your sessions must not thereby be able to
     * talk to them, and installing one that can is a decision somebody makes by
     * reading the capability.
     */
    const isSessionWrite = method === 'sessions.send'
    // A WRITE needs the write grant; the read grant alone is not enough. Mirrors
    // the comments namespace, where canWrite and canRead are separate.
    if (isDocWrite) {
      if (!writeGrants.includes('documents')) {
        throw new Error('this app is not granted "documents" write access')
      }
    } else if (isSessionWrite) {
      if (!writeGrants.includes('sessions')) {
        throw new Error('this app is not granted "sessions" write access')
      }
    } else if (!grants.includes(namespace)) {
      throw new Error(`this app is not granted "${namespace}" access`)
    }

    /** Documents this app may touch: THIS project's catalog, excluding app
     *  instances (they are app data, reached via mod.type). Same scoping the
     *  reads use, so a write cannot reach a foreign id either. */
    const inCatalog = (id: string): boolean =>
      projectDocuments(ws.doc()).some((d) => d.id === id && !d.dataType)

    switch (method) {
      case 'docs.create': {
        const path = String(args[0] ?? '').trim()
        if (!path) throw new Error('path is required')
        const content = String(args[1] ?? '')
        const handle = await createDocument(repo, projectId, {
          ...personChangeMessage(),
          path,
          mime: mimeFromPath(path),
          content,
        })
        return { id: handle.documentId as string, path }
      }
      case 'docs.open': {
        const id = String(args[0] ?? '')
        // A source file is an open item, not a catalog document. Navigation
        // hands it to the host Code pane without serving its contents to the
        // app. The source must belong to this project (RFC 0356 S1).
        const code = parseCodeId(id)
        if (code) {
          const source = projectSources(ws.doc()).sources.some(
            (entry) => entry.id === code.sourceId,
          )
          if (!source) throw new Error('that source is not in this project')
          nav.openDocument?.(id)
          return { opened: id }
        }
        // Scoped to THIS project's catalog, so navigation cannot be used to
        // probe for ids the app was never given. INSTANCES INCLUDED, unlike
        // every read here: opening one shows the owning app's view, so nothing
        // of another app's data crosses into this one. The reads exclude
        // instances because reading is how an app would take that data; a
        // person being taken to a week is not.
        //
        // Excluding them made mod.candidates (RFC 0292) a half-feature: it
        // offers every document in the catalog, so a view could name a week or
        // a canvas and then fail to follow the chip it had just drawn.
        const known = projectDocuments(ws.doc()).some((d) => d.id === id)
        if (!id || !known) {
          throw new Error('that document is not in this project')
        }
        nav.openDocument?.(id)
        return { opened: id }
      }
      case 'docs.write': {
        const id = String(args[0] ?? '')
        const content = String(args[1] ?? '')
        if (!id || !inCatalog(id)) return null
        // No overwriteGenerated on any bridge path, deliberately: converting
        // build output back to prose is a human act, not something one app
        // does to another app's published view. writeDocumentContent refuses.
        await writeDocumentContent(repo, projectId, id, content)
        return { id, length: content.length }
      }
      case 'docs.patch': {
        const id = String(args[0] ?? '')
        const oldString = String(args[1] ?? '')
        const newString = String(args[2] ?? '')
        if (!id || !inCatalog(id) || !oldString) return null
        const handle = await repo.find(id as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        const before = readDocumentContent(handle.doc())
        const count = before.split(oldString).length - 1
        // Refuse rather than guess: a miss or an ambiguous match are both
        // reported so the app can widen its context, exactly as the agent-side
        // patch tool does.
        if (count === 0) return { error: 'not found' }
        if (count > 1) return { error: `ambiguous: ${count} occurrences` }
        // Literal splice, NEVER String.replace: its $-sequences expand in the
        // replacement and silently corrupt documents containing $.
        const at = before.indexOf(oldString)
        const after =
          before.slice(0, at) + newString + before.slice(at + oldString.length)
        await writeDocumentContent(repo, projectId, id, after)
        return { id, length: after.length }
      }
      case 'docs.list':
        // The project document catalog (RFC 0263). App instances are the app's
        // own data (reachable via mod.type), not "documents", so exclude them.
        return projectDocuments(ws.doc())
          .filter((d) => !d.dataType)
          .map((d) => ({
            id: d.id,
            path: d.path,
            title: d.title,
            mime: d.mime,
          }))
      case 'docs.read': {
        const id = String(args[0] ?? '')
        if (!id) return null
        // Scope the read to THIS project's catalog: the grant is a namespace,
        // not a licence to read any repo id. A view that hardcodes a foreign id
        // (or a session id) gets nothing. App instances are excluded here too —
        // they are app data, read via mod.type, not "documents".
        const inCatalog = projectDocuments(ws.doc()).some(
          (d) => d.id === id && !d.dataType,
        )
        if (!inCatalog) return null
        const doc = await repo.find(id as AnyDocumentId)
        await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
        return readDocumentContent(doc.doc())
      }
      case 'sessions.list':
        return projectSessions(ws.doc())
      case 'sessions.read': {
        const id = String(args[0] ?? '')
        if (!id) return null
        // Same project-scoping as docs.read: only sessions in this
        // project's registry are readable, never an arbitrary repo id.
        const inRegistry = projectSessions(ws.doc()).some((s) => s.id === id)
        if (!inRegistry) return null
        const doc = await repo.find(id as AnyDocumentId)
        await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
        return projectSessionTranscript(doc.doc())
      }
      case 'sessions.send': {
        /**
         * Say something to a session, as a person would.
         *
         * The message lands as a USER message on that session's log and queues
         * a run, through `enqueueRun` — the same call the composer and the
         * agent's own `send_message` make. One path, because it is the only one
         * that both appends the message and queues the turn, and that knows a
         * captured session must not get a run. A second implementation here is
         * how an app's messages would quietly stop being answered.
         *
         * PROJECT-SCOPED, exactly as `sessions.read` is: only a session this
         * project's registry lists. Otherwise an app that guessed an id could
         * talk into a conversation in a project it was never installed in.
         */
        const id = String(args[0] ?? '').trim()
        const text = String(args[1] ?? '').trim()
        if (!id) throw new Error('a session id is required')
        if (!text) throw new Error('a message is required')
        if (!projectSessions(ws.doc()).some((s) => s.id === id)) {
          throw new Error('that session is not in this project')
        }
        await enqueueRun(repo, id, text, { projectId: projectId })
        return { id }
      }
      case 'sessions.open': {
        // A NAVIGATION, not a read: hand the session to the shell's agent panel.
        // Still project-scoped and under the "sessions" grant. A no-op if the
        // host provided no navigator (e.g. a standalone embedder).
        const id = String(args[0] ?? '')
        if (!id) return null
        if (!projectSessions(ws.doc()).some((s) => s.id === id)) {
          throw new Error('that session is not in this project')
        }
        nav.openSession?.(id)
        return { id }
      }
      default:
        throw new Error(`unknown method: ${method}`)
    }
  }

  // Callers spread this then set `id` LAST, so an instance whose data happens
  // to have an `id` field can't clobber the real document id.
  const readFields = (doc: unknown) => readDocumentData(doc)

  /**
   * Refuse a write that would CHANGE a field the app declared write-once
   * (`schema.immutable`, RFC 0271).
   *
   * Write-once, not read-only: setting a field that has no value yet is how a
   * bare record acquires its branch in its own empty state. Only changing an
   * existing value is refused, and writing the SAME value is allowed, so a
   * view that echoes the whole record back does not trip on itself.
   *
   * Here rather than in the view because a view convention holds only for
   * people clicking a control. This is the layer an app writes through, so it
   * is the layer where "this cannot change" can be true.
   *
   * The MCP tools stay open on purpose: repairing a corrupt record means
   * rewriting exactly these fields, and a rule with no repair channel turns a
   * bad record into one that can only be deleted.
   */
  const refuseFrozenWrites = async (
    doc: unknown,
    patch: Record<string, unknown>,
  ): Promise<void> => {
    // The declaration lives on the app DOC, not the thin catalog entry, so it
    // is hydrated here. Writes are rare enough that one find per write is
    // cheaper than carrying the schema through every call site.
    // The World's apps write here too (RFC 0338 3g).
    const worldId = readWorldPointer()
    const world = worldId
      ? (await repo.find(worldId as AnyDocumentId)).doc()
      : undefined
    const summary = installedApps(world, ws.doc()).find(
      (a) => a.dataType === dataType,
    )
    if (!summary) return
    const appHandle = await repo.find(summary.id as AnyDocumentId)
    await (appHandle as { whenReady?: () => Promise<void> }).whenReady?.()
    const frozen = readApp(appHandle.doc())?.schema?.immutable
    if (!Array.isArray(frozen) || frozen.length === 0) return
    const current = readDocumentData(doc) ?? {}
    for (const field of frozen) {
      if (!(field in patch)) continue
      const was = current[field]
      const now = patch[field]
      const hadValue = was !== undefined && was !== null && was !== ''
      if (hadValue && was !== now) {
        throw new Error(
          `"${field}" is set once and cannot be changed (it is ${JSON.stringify(was)}). Create a new record instead.`,
        )
      }
    }
  }

  // The capability boundary: a frame supplies an id, but it may only reach a
  // document that is an instance of THIS app's dataType in THIS project.
  // Without this, get/update on an arbitrary id would read or overwrite any
  // document in the repo (another app's instances, sessions) — the sandbox +
  // bridge exist precisely to prevent that.
  const requireOwnInstance = async (id: string): Promise<DocumentDoc> => {
    if (!id) throw new Error('a document id is required')
    const doc = await repo.find<DocumentDoc>(id as AnyDocumentId)
    await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
    const snap = doc.doc() as { dataType?: unknown } | undefined
    // The catalog is the current authority for project membership. A project
    // moved to another device may keep the old stored workspaceId on its
    // documents; rejecting that field made its existing Canvas records
    // unreadable even though they are listed in this project's catalog.
    const listedHere = projectDocumentsOfType(ws.doc(), dataType).some(
      (ref) => ref.id === id,
    )
    if (!snap || snap.dataType !== dataType || !listedHere) {
      throw new Error(`${id} is not a "${dataType}" instance in this project`)
    }
    return doc.doc() as DocumentDoc
  }

  switch (method) {
    case 'list': {
      const refs = projectDocumentsOfType(ws.doc(), dataType)
      // allSettled, not all: one instance that is slow or unavailable must not
      // blank the whole list. Drop the failures; return what resolved.
      const settled = await Promise.allSettled(
        refs.map(async (r) => {
          const doc = await repo.find<DocumentDoc>(r.id as AnyDocumentId)
          await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
          return { ...readFields(doc.doc()), id: r.id }
        }),
      )
      return settled
        .filter(
          (
            s,
          ): s is PromiseFulfilledResult<
            Record<string, unknown> & { id: string }
          > => s.status === 'fulfilled',
        )
        .map((s) => s.value)
    }
    case 'read':
    case 'get': {
      const id = String(args[0] ?? '')
      if (!id) return null
      const doc = await requireOwnInstance(id)
      return { ...readFields(doc), id }
    }
    case 'create': {
      const data =
        args[0] && typeof args[0] === 'object'
          ? (args[0] as Record<string, unknown>)
          : {}
      // RFC 0338 3e: an app that knows where its records belong may say so;
      // without a folder a record lands under its data type, as it always has.
      const opts =
        args[1] && typeof args[1] === 'object'
          ? (args[1] as { folder?: unknown })
          : {}
      const folder =
        typeof opts.folder === 'string'
          ? normalizeDocumentPath(opts.folder)
          : null
      const handle = await createDocument(repo, projectId, {
        ...personChangeMessage(),
        path: `${folder ?? dataType}/${newInstanceSlug()}`,
        mime: 'application/mod-instance',
        dataType,
        data,
      })
      return { id: handle.documentId as string }
    }
    case 'readBody': {
      const id = String(args[0] ?? '')
      if (!id) return null
      const doc = await requireOwnInstance(id)
      return { id, content: readDocumentContent(doc) }
    }
    case 'writeBody': {
      // MERGES rather than replaces: writeDocumentContent splices via updateText, so
      // two people editing a plan in different regions both keep their edits.
      // That is what lets this back a live editor instead of a
      // last-writer-wins text box.
      const id = String(args[0] ?? '')
      const text = String(args[1] ?? '')
      await requireOwnInstance(id) // scope check before the write
      await writeDocumentContent(repo, projectId, id, text)
      return { id, length: text.length }
    }
    case 'change': {
      // RFC 0267 change(id, patch): MERGE the patch, preserving omitted fields.
      const id = String(args[0] ?? '')
      const patch =
        args[1] && typeof args[1] === 'object'
          ? (args[1] as Record<string, unknown>)
          : {}
      const doc = await requireOwnInstance(id) // scope check before the write
      await refuseFrozenWrites(doc, patch)
      // Stamped with the person, so a record change in an app's view has a
      // name in history (0313 adversarial review, finding 8).
      await patchDocumentData(repo, projectId, id, patch, personChangeMessage())
      return { id }
    }
    case 'update': {
      // Whole-value replace (explicit escape hatch, not the RFC default).
      const id = String(args[0] ?? '')
      const data =
        args[1] && typeof args[1] === 'object'
          ? (args[1] as Record<string, unknown>)
          : {}
      const doc = await requireOwnInstance(id) // scope check before the write
      // Whole-value replace still cannot rewrite a frozen field, or `update`
      // would be the way round `change`'s rule.
      await refuseFrozenWrites(doc, data)
      await writeDocumentData(repo, projectId, id, data, personChangeMessage())
      return { id }
    }
    case 'rename': {
      // The catalog title, not a data field. An instance is created with a path
      // (`review/d1i1ix6c`) and no title, and `projectDocuments` falls back to
      // that path — so without this every instance is listed by its slug no
      // matter what the record calls itself. Scoped like change/update: an app
      // can only retitle its own type's records.
      const id = String(args[0] ?? '')
      const title = String(args[1] ?? '').trim()
      await requireOwnInstance(id)
      if (!title) return { id }
      // IDEMPOTENT, because the app cannot check for itself: the catalog title
      // is not part of an instance's data, so a view has no way to read the
      // current one and skip a redundant write. Without this guard, an app that
      // reconciles its title on open writes to the project doc every time a
      // record is opened, for no change.
      const current = projectDocuments(ws.doc()).find((d) => d.id === id)
      if (current?.title === title) return { id }
      await renameDocument(repo, projectId, id, title)
      return { id }
    }
    case 'open': {
      // Host NAVIGATION to a record of this type, the scoped twin of
      // `docs.open`. A view that switched records in its own state left the
      // host believing every one of them was the app's view: a reload went
      // back to the list, a link could not reach a record already on screen,
      // and no session could be said to hold the one you were reading.
      const id = String(args[0] ?? '')
      await requireOwnInstance(id)
      nav.openDocument?.(id)
      return { opened: id }
    }
    case 'delete': {
      // The catalog unlink, which is what "delete" means for an instance: the
      // row goes, the record document is left for reachability GC. Scoped
      // first, so a frame cannot pass an arbitrary id and unlist a session or
      // another app's records.
      const id = String(args[0] ?? '')
      await requireOwnInstance(id)
      await deleteDocument(repo, projectId, id)
      return { id }
    }
    default:
      throw new Error(`unknown mod.type method: ${method}`)
  }
}

/** A short path segment for a new instance (display only; identity is the doc
 *  id). */
function newInstanceSlug(): string {
  return Math.random().toString(36).slice(2, 10)
}

export type { BridgeCall }
